import React from 'react';
import { connect } from 'react-redux';
import { Trans, useTranslation } from 'react-i18next';
import { sendEvent, buildEventPayload } from '../../../../api/analytics-client';
import './style.scss';

interface ConsentOverlayProps {
    buttonText?: string;
    buttonColor?: string;
    privacyLinkUrl?: string;
    privacyLinkText?: string;
    storageKey?: string;
    text?: string | React.ReactNode;
    analyticsServiceUrl?: string | null;
    sessionId?: string | null;
}

// Neutral fallback — themes enabling consent should always set their own
// project-specific storageKey so consent state survives theme changes.
const FALLBACK_STORAGE_KEY = 'cui-widget_consent_accepted';

const ConsentOverlay: React.FC<ConsentOverlayProps> = ({
    buttonText,
    buttonColor,
    privacyLinkUrl = '#',
    privacyLinkText,
    storageKey,
    text,
    analyticsServiceUrl,
    sessionId,
}) => {
    const { t } = useTranslation();
    const resolvedButtonText = buttonText || t('consent.buttonText');
    const resolvedPrivacyLinkText = privacyLinkText || t('consent.privacyLinkText');
    const effectiveStorageKey = storageKey || FALLBACK_STORAGE_KEY;

    const [accepted, setAccepted] = React.useState(() => {
        if (!storageKey) {
            console.warn(
                `[CUI Widget] theme.consent.storageKey is not set — using fallback "${FALLBACK_STORAGE_KEY}"`
            );
        }
        try {
            return localStorage.getItem(effectiveStorageKey) === 'true';
        } catch {
            return false;
        }
    });

    const handleAccept = React.useCallback(() => {
        try {
            localStorage.setItem(effectiveStorageKey, 'true');
        } catch {
            // localStorage not available
        }
        setAccepted(true);

        if (analyticsServiceUrl) {
            sendEvent(
                analyticsServiceUrl,
                buildEventPayload(sessionId || 'anonymous', 'consent_accepted')
            );
        }
    }, [effectiveStorageKey, analyticsServiceUrl, sessionId]);

    if (accepted) {
        return null;
    }

    return (
        <div className="rw-consent-overlay">
            <div className="rw-consent-content">
                {text ? (
                    <div className="rw-consent-text">{text}</div>
                ) : (
                    <div className="rw-consent-text">
                        <p>
                            <Trans
                                i18nKey="consent.intro"
                                values={{
                                    buttonText: resolvedButtonText,
                                    privacyLinkText: resolvedPrivacyLinkText,
                                }}
                                components={{
                                    privacyLink: (
                                        <a
                                            href={privacyLinkUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        />
                                    ),
                                }}
                            />
                        </p>
                        <p>{t('consent.revocation')}</p>
                    </div>
                )}
                <button
                    className="rw-consent-accept-btn"
                    style={{ backgroundColor: buttonColor || 'var(--theme-color-primary)' }}
                    onClick={handleAccept}
                    type="button"
                >
                    <span>{resolvedButtonText}</span>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path
                            d="M3 8H13M13 8L9 4M13 8L9 12"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                </button>
            </div>
        </div>
    );
};

const mapStateToProps = (state: any) => ({
    analyticsServiceUrl: state.config.get('analyticsServiceUrl') || null,
    sessionId: state.config.get('sessionId') || null,
});

export default connect(mapStateToProps)(ConsentOverlay);
